import { createEventHook } from '@vueuse/core';
import { type BasicMenuListItemProps } from '@molecules/BasicMenu/BasicMenuListItem.vue';

export type SelectOptionType = {
    name: string;
    index: number;
};

export type UseSelectNavigationParams = {
    items: Readonly<Ref<BasicMenuListItemProps[]>>;
    modelValue: Readonly<Ref<string | number>>;
};

export const useSelectNavigation = (params: UseSelectNavigationParams) => {
    const { items, modelValue } = params;

    const isOpen = ref(false);
    const isFocused = ref(false);
    const highlighted = ref<null | SelectOptionType>(null);

    const onValueChange = createEventHook<string>();

    const setIsOpen = (value: boolean) => (isOpen.value = value);
    const setIsFocused = (value: boolean) => (isFocused.value = value);

    const setHighlighted = (index: number) => {
        highlighted.value = {
            index,
            name: items.value[index].name,
        };
    };

    const handleArrowUp = (name?: string, index?: number) => {
        if (isOpen.value && name && index && index > 0) {
            setHighlighted(index - 1);
            return;
        }

        if (!isOpen.value && isFocused.value && modelValue.value) {
            const foundIdx = items.value.findIndex(
                (item) => item.name === modelValue.value
            );
            const found = items.value.find(
                (item) => item.name === modelValue.value
            );
            if (found && foundIdx > 0) {
                return onValueChange.trigger(items.value[foundIdx - 1].name);
            }
        }
    };

    const handleArrowDown = (index?: number) => {
        if (
            isOpen.value &&
            index !== undefined &&
            index < items.value.length - 1
        ) {
            setHighlighted(index + 1);
            return;
        }
        if (!isOpen.value && isFocused.value) {
            if (modelValue.value) {
                const foundIdx = items.value.findIndex(
                    (item) => item.name === modelValue.value
                );
                const found = items.value.find(
                    (item) => item.name === modelValue.value
                );
                if (found && foundIdx < items.value.length - 1) {
                    return onValueChange.trigger(
                        items.value[foundIdx + 1].name
                    );
                }
            } else {
                return onValueChange.trigger(items.value[0].name);
            }
            return;
        }
        if (!highlighted.value) {
            setHighlighted(0);
        }
    };

    const handleEnter = () => {
        const name = highlighted.value?.name;

        if (name && name !== modelValue.value && isOpen.value) {
            setIsOpen(false);
            return onValueChange.trigger(name);
        }
        if (isFocused.value && !isOpen.value) {
            setIsOpen(true);
        }
    };

    const handleEscape = () => setIsOpen(false);

    const methodsDict = {
        Escape: handleEscape,
        Enter: handleEnter,
        ArrowDown: async () => {
            await handleArrowDown(highlighted.value?.index);
        },
        ArrowUp: async () => {
            await handleArrowUp(
                highlighted.value?.name,
                highlighted.value?.index
            );
        },
    };

    const selectNavigationListener = async (event: KeyboardEvent) => {
        const { key } = event;

        if (isOpen.value && key === 'Tab') {
            event.preventDefault();
        } else if (!isOpen.value && key === 'Tab') {
            setIsFocused(false);
        }

        if (methodsDict[key as keyof typeof methodsDict]) {
            event.preventDefault();
            await methodsDict[key as keyof typeof methodsDict]();
        }
    };

    return {
        highlighted,
        isFocused,
        isOpen,
        setIsOpen,
        setIsFocused,
        selectNavigationListener,
        onValueChange: onValueChange.on,
    };
};
